/**
 * Single, dependency-free Instagram post-URL owner (cas-79cf, cas-ef8b).
 * Imported directly by frontend utilities as well as backend callers.
 * URL recognition and caller-specific shortcode policies are separate:
 * display/lookup keeps the whole identifier; intake strips share suffixes;
 * the provider boundary never guesses or sends malformed shortcodes.
 * See instagram-url.util.spec.ts and docs/INSTAGRAM_URL_DOMAIN.md.
 */
const SHORTCODE_PATTERN = /^[A-Za-z0-9_-]{5,30}$/;
const SHORTCODE_CHARACTERS = /^[A-Za-z0-9_-]+$/;
const USERNAME_PATTERN = /^[A-Za-z0-9._]+$/;
const RESERVED_SEGMENTS = new Set(['share', 'stories', 'explore', 'accounts', 'direct', 's']);

export type InstagramPostUrl = {
  shortcode: string;
  kind: 'p' | 'reel' | 'tv';
  username: string | null;
};

/** Host checks are shared; lookalike hosts and embedded URLs are not Instagram. */
export function isInstagramHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'instagram.com' ||
    host.endsWith('.instagram.com') ||
    host === 'instagr.am' ||
    host.endsWith('.instagr.am')
  );
}

function postKind(segment: string): InstagramPostUrl['kind'] | null {
  switch (segment.toLowerCase()) {
    case 'p':
      return 'p';
    case 'reel':
    case 'reels':
      return 'reel';
    case 'tv':
      return 'tv';
    default:
      return null;
  }
}

/**
 * Recognize root or username-attributed /p/, /reel(s)/ and /tv/ URLs.
 * Query/fragment are URL metadata, never part of the identifier. The entire
 * path segment is returned so validation cannot silently accept a valid prefix
 * of a malformed identifier. Bare shortcodes are not URLs.
 */
export function parseInstagramPostUrl(input?: string | null): InstagramPostUrl | null {
  const raw = input?.trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  if (!['http:', 'https:'].includes(url.protocol) || !isInstagramHostname(url.hostname)) {
    return null;
  }
  const segments = url.pathname.slice(1).split('/');
  const first = segments[0] || '';
  if (RESERVED_SEGMENTS.has(first.toLowerCase())) return null;
  const rootKind = postKind(first);
  const kind = rootKind ?? postKind(segments[1] || '');
  if (!kind || (!rootKind && !USERNAME_PATTERN.test(first))) return null;
  const shortcode = segments[rootKind ? 1 : 2];
  if (!shortcode) return null;
  return { shortcode, kind, username: rootKind ? null : first };
}

/** Legacy display/lookup policy: no length trimming or provider minimum. */
export function extractInstagramUrlShortcode(input?: string | null): string | null {
  return parseInstagramPostUrl(input)?.shortcode ?? null;
}

/**
 * SMS/intake policy (cas-66a5, cas-613b, cas-34c1): share suffixes are stripped
 * to 11 characters; /p/ requires all 11, while reel/tv keeps shorter IDs.
 * Validation applies to the canonical prefix, as it did before consolidation.
 */
export function extractInstagramIntakeShortcode(input?: string | null): string | null {
  const post = parseInstagramPostUrl(input);
  if (!post) return null;
  const code = post.shortcode.slice(0, 11);
  if (!SHORTCODE_CHARACTERS.test(code) || (post.kind === 'p' && code.length !== 11)) return null;
  return code;
}

/** Collapse only the Instagram post route, preserving username and code case. */
export function normalizeInstagramPostPath(url: URL): string {
  const post = parseInstagramPostUrl(url.href);
  if (!post || post.kind !== 'reel') return url.pathname;
  const segments = url.pathname.split('/');
  segments[post.username === null ? 1 : 2] = 'reel';
  return segments.join('/');
}

/** True when a string is shaped like a shortcode we can send to the provider. */
export function isInstagramShortcodeShaped(value?: string | null): boolean {
  return typeof value === 'string' && SHORTCODE_PATTERN.test(value);
}

/** Provider policy: complete 5..30-character code or null; never a URL prefix. */
export function extractInstagramShortcode(input?: string | null): string | null {
  const raw = input?.trim();
  if (!raw) return null;
  const candidate = extractInstagramUrlShortcode(raw) ?? raw;
  return isInstagramShortcodeShaped(candidate) ? candidate : null;
}

export const UNSUPPORTED_INSTAGRAM_LINK_MESSAGE =
  'That Instagram link form is not supported. Use the post, reel, or share link that opens the post itself.';
